import type { ReactNode } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useConfirm } from '@/components/ui/confirm';
import { useHasAnyRole } from '@/auth/hooks';
import { AppRole } from '@/auth/roles';
import type { AdminPostManagerDetailResponseDTO } from '@/api/generated/model';
import { useUpdatePostStatus } from '../api';
import { toast } from 'sonner';
import {
  contentTypeLabel,
  formatUsd,
  isPostHidden,
  isVideoFileType,
  moderationStatusBadgeVariant,
  moderationStatusLabel,
  normalizeModerationStatusKey,
  displayModerationStatus,
  postPublicationHint,
  postStatusBadgeVariant,
  postStatusLabel,
  formatElapsedSince,
  scanProcessingStartedAt,
  categoryLabel,
} from '../lib/contentDisplay';

interface ContentSummaryCardProps {
  contentId: string;
  detail: AdminPostManagerDetailResponseDTO;
}

const NA = 'N/A';

function formatDate(iso?: string | null): string {
  if (!iso) return NA;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

function formatDevice(deviceName?: string | null, osName?: string | null): string {
  const parts = [deviceName, osName].filter((p): p is string => !!p && p.trim() !== '');
  return parts.length > 0 ? parts.join(' + ') : NA;
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex justify-between gap-4 py-1.5 text-sm">
      <span className="shrink-0 text-muted-foreground">{label}</span>
      <span className="min-w-0 text-right">{children}</span>
    </div>
  );
}

export function ContentSummaryCard({ contentId, detail }: ContentSummaryCardProps) {
  const confirm = useConfirm();
  const mutation = useUpdatePostStatus(contentId);
  // Defense in depth — the route already restricts these roles (doc 03 / §4.9).
  const canModerate = useHasAnyRole([
    AppRole.Administrator,
    AppRole.SystemAdmin,
    AppRole.ContentManager,
  ]);

  // The admin detail DTO now exposes postStatus, so the toggle direction is known: a hidden post is
  // re-listed (-> PUB), everything else can be taken down (-> HID).
  const hidden = isPostHidden(detail.postStatus);
  const harness = detail.adminPipelineHarness === true;
  const listBlocked = hidden && harness;

  async function onToggle() {
    if (listBlocked) return;
    const { confirmed } = await confirm({
      title: 'Confirm',
      description: hidden
        ? 'Are you sure you want to list this content again?'
        : 'Are you sure you want to take down this content?',
      confirmText: hidden ? 'List Content' : 'Take Down',
      variant: hidden ? 'default' : 'destructive',
    });
    if (!confirmed) return;
    try {
      await mutation.mutateAsync({
        postId: Number(contentId),
        params: { postStatus: hidden ? 'PUB' : 'HID' },
      });
      toast.success(hidden ? 'Content listed.' : 'Content taken down.');
    } catch {
      toast.error(hidden ? 'Failed to list content.' : 'Failed to take down content.');
    }
  }

  // Title / displayName not in real DTO — fall back to content only.
  const title = detail.content?.trim() || NA;
  // tags is a plain string on the real DTO (not an array).
  const tags = detail.tags?.trim() || NA;
  const categories = (detail.categories ?? [])
    .map((c) => categoryLabel(c))
    .filter((c): c is string => !!c);
  const collaborator = detail.collaboratorDisplayName?.trim() || detail.collaboratorUName?.trim();
  const publicationHint = postPublicationHint(detail.postStatus);
  // Detail OpenAPI may lag admin-api; list already returns these ledger risk fields.
  const detailRisk = detail as AdminPostManagerDetailResponseDTO & {
    combinedRisk?: number | null;
    riskTier?: string | null;
  };
  const status = displayModerationStatus(detail.moderationStatus, {
    combinedRisk: detailRisk.combinedRisk,
    riskTier: detailRisk.riskTier,
  });
  const ledgerKey = normalizeModerationStatusKey(status);
  const startedAt = scanProcessingStartedAt({
    moderationStartedAt: detail.moderationStartedAt ?? null,
  });
  const elapsed = ledgerKey === 'PENDING' ? formatElapsedSince(startedAt) : null;
  // TipMi ledger is publication state only — never Bedrock classify / is_add_category.
  const ledgerError =
    ledgerKey === 'ERROR' ? (detail.moderationLastError ?? '').trim() || null : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Content Summary</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="divide-y">
          <Field label="Post ID">
            <span className="font-mono tabular-nums">{contentId}</span>
          </Field>
          <Field label="Title / Content">{title}</Field>
          <Field label="Content Type">
            {contentTypeLabel(
              detail.postType,
              detail.medias?.find((m) => isVideoFileType(m.fileType))?.fileType ??
                detail.medias?.[0]?.fileType,
            )}
          </Field>
          <Field label="TipMi visibility">
            {detail.postStatus ? (
              <span className="inline-flex flex-col items-end gap-0.5">
                <Badge variant={postStatusBadgeVariant(detail.postStatus)}>
                  {postStatusLabel(detail.postStatus)}
                </Badge>
                {publicationHint ? (
                  <span className="text-xs text-muted-foreground">{publicationHint}</span>
                ) : (
                  <span className="text-xs text-muted-foreground">Feed / hold state</span>
                )}
              </span>
            ) : (
              NA
            )}
          </Field>
          <Field label="TipMi ledger">
            <span className="inline-flex flex-col items-end gap-0.5">
              <Badge variant={moderationStatusBadgeVariant(status)}>
                {moderationStatusLabel(status)}
              </Badge>
              {elapsed ? (
                <span className="text-xs text-muted-foreground">Pending · {elapsed}</span>
              ) : (
                <span className="text-xs text-muted-foreground">Publication ledger</span>
              )}
              {ledgerError ? (
                <span
                  className="max-w-[240px] text-xs text-destructive break-words text-right"
                  title={ledgerError}
                >
                  {ledgerError}
                </span>
              ) : null}
            </span>
          </Field>
          <Field label="Upload Date">{formatDate(detail.createdAt)}</Field>
          <Field label="Price">{detail.price != null ? formatUsd(detail.price) : NA}</Field>
          <Field label="Country">{detail.location || NA}</Field>
          <Field label="Device">{formatDevice(detail.deviceName, detail.osName)}</Field>
          {collaborator && <Field label="Collaborator">{collaborator}</Field>}
          <Field label="Categories">
            {categories.length > 0 ? (
              <span className="inline-flex flex-wrap justify-end gap-1">
                {categories.map((name) => (
                  <Badge key={name} variant="secondary" className="font-normal">
                    {name}
                  </Badge>
                ))}
              </span>
            ) : (
              NA
            )}
          </Field>
          <Field label="Hashtags">{tags}</Field>
        </div>

        {canModerate && (
          <Button
            className="mt-4 w-full"
            variant={hidden ? 'default' : 'destructive'}
            disabled={mutation.isPending || listBlocked}
            title={
              listBlocked
                ? 'Pipeline Test harness content cannot be published. Use a visible (non-pipeline-only) test post instead.'
                : undefined
            }
            onClick={() => void onToggle()}
          >
            {hidden ? 'List Content' : 'Take Down'}
          </Button>
        )}
        {listBlocked && (
          <p className="mt-2 text-xs text-muted-foreground">
            Pipeline Test harness — listing/publishing is disabled.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
